import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { findUom, formatUomLabel, searchUom, type UomOption } from "@/lib/uom";

interface UomComboboxProps {
  /** The stored LHDN unit code. */
  value: string | null | undefined;
  onChange: (code: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  invalid?: boolean;
}

/** How many matches to render at once  */
const RESULT_LIMIT = 50;

/**
 * Searchable picker over the LHDN unit-of-measurement code list.
 */
export const UomCombobox = ({
  value,
  onChange,
  disabled,
  placeholder = "Select unit",
  className,
  invalid,
}: UomComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const results = useMemo(() => searchUom(query, RESULT_LIMIT), [query]);

  const isUnrecognised = Boolean(value) && !findUom(value);

  useEffect(() => {
    if (open) {
      // Let the popover mount before taking focus
      const id = window.setTimeout(() => searchRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    setQuery("");
  }, [open]);

  const select = (option: UomOption) => {
    onChange(option.code);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "h-10 w-48 justify-between font-normal",
            !value && "text-muted-foreground",
            isUnrecognised &&
              "border-amber-500 text-amber-700 dark:text-amber-400",
            invalid && "border-destructive",
            className,
          )}
          title={
            isUnrecognised
              ? `"${value}" is not an LHDN unit code — pick a unit so it can be sent to LHDN`
              : formatUomLabel(value) || placeholder
          }
        >
          <span className="truncate">
            {formatUomLabel(value) || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[22rem] p-0" align="start">
        <div className="flex items-center gap-2 border-b px-3">
          <Search className="h-4 w-4 shrink-0 opacity-50" />
          <Input
            ref={searchRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by code or name, e.g. A68 or exajoule"
            className="h-10 border-0 px-0 shadow-none focus-visible:ring-0"
          />
          {value ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              title="Clear unit"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>

        <div className="max-h-72 overflow-y-auto p-1">
          {results.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              No unit matches “{query}”.
            </p>
          ) : (
            results.map((option) => (
              <button
                key={option.code}
                type="button"
                onClick={() => select(option)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm",
                  "hover:bg-accent hover:text-accent-foreground",
                  option.code === value && "bg-accent/50",
                )}
              >
                <Check
                  className={cn(
                    "h-4 w-4 shrink-0",
                    option.code === value ? "opacity-100" : "opacity-0",
                  )}
                />
                <span className="w-12 shrink-0 font-mono text-xs font-semibold">
                  {option.code}
                </span>
                <span className="truncate">{option.name}</span>
              </button>
            ))
          )}
        </div>

        {results.length >= RESULT_LIMIT ? (
          <p className="border-t px-3 py-2 text-xs text-muted-foreground">
            Showing the first {RESULT_LIMIT} matches — keep typing to narrow
            them.
          </p>
        ) : null}
      </PopoverContent>
    </Popover>
  );
};

export default UomCombobox;
